import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { Feature, GeoJsonProperties, Geometry } from "geojson";

import { DatasetEntryFormData } from "@/components/spatialAnalysis/saveAsDatasetPopup";
import { IOptionItem } from "@/components/ui/dropdown/types";
import { AdminAreaData } from "@/types/adminArea";
import {
  Dataset,
  DatasetAttributeSchema,
  DatasetData,
  DatasetFilter
} from "@/types/dataset";
import { MarkerPinItem } from "@/types/map";
import {
  AdminAreaSettings,
  CatchmentSettings,
  ECatchmentInputBy,
  ECatchmentType,
  EOutputAnalysisType,
  ERoadAccessMeasurement,
  PolygonSettingItem,
  RoadAccessConfig,
  ScoringOptionsEnum,
  SpatialConfig
} from "@/types/spatial";

import { v4 as uuidv4 } from "uuid";

export interface SpatialState {
  currRunningAnalysisId?: string;
  latestAnalysisId?: string;
  isEdit?: boolean;
  currentConfigId?: string;
  title?: string;
  description?: string;
  adminAreaSettings: AdminAreaSettings | null;
  adminAreaSettingsData: AdminAreaData | null;
  adminAreaSettingsDataTemp: AdminAreaData | null;
  catchmentSettings: CatchmentSettings | null;
  catchmentSettingsTemp: CatchmentSettings;
  polygonSettings: Record<string, PolygonSettingItem>;
  polygonSettingsTemp: Record<string, PolygonSettingItem>;
  drawnPolygon?: Record<string, Feature<Geometry, GeoJsonProperties>>;
  spatialConfig: SpatialConfig;
  latestSaveAsDataset?: DatasetEntryFormData | null;
  isShowMarkerLabel: boolean;
  adminTreeSettings: {
    selectedAreaIds: string[];
  };
  adminTreeSettingsTemp: {
    selectedAreaIds: string[];
  };
  overrideGlobalFilter: boolean;
}

export const initialCatchmentSetting: CatchmentSettings = {
  catchmentInputBy: ECatchmentInputBy.BY_LOCATION,
  catchmentType: ECatchmentType.BY_RADIUS,
  radiusData: [],
  placeCoordinates: [],
  roadAccessConfig: {
    measurementType: ERoadAccessMeasurement.DISTANCE,
    distances: [" "],
    durations: [" "]
  },
  datasetConfig: {
    datasets: {},
    datasetIndices: {},
    visualizationOrder: [],
    selectedDataset: null
  }
};

export const spatialInitialState: SpatialState = {
  isEdit: false,
  adminAreaSettings: null,
  adminAreaSettingsData: null,
  adminAreaSettingsDataTemp: null,
  catchmentSettings: initialCatchmentSetting,
  catchmentSettingsTemp: initialCatchmentSetting,
  polygonSettings: {},
  polygonSettingsTemp: {},
  spatialConfig: {
    areaInput: null,
    outputType: EOutputAnalysisType.GRID,
    gridType: null,
    gridResolution: null,
    h3Resolution: null,
    scoringOption: ScoringOptionsEnum.NORMALIZED,
    areaSize: 0
  },
  isShowMarkerLabel: false,
  adminTreeSettings: {
    selectedAreaIds: []
  },
  adminTreeSettingsTemp: {
    selectedAreaIds: []
  },
  overrideGlobalFilter: false
};

export const spatialSlice = createSlice({
  name: "spatial",
  initialState: spatialInitialState,
  reducers: {
    setAreaInput: (state, action: PayloadAction<IOptionItem | null>) => {
      state.spatialConfig.areaInput = action.payload;
    },
    setIsShowMarkerLabel: (
      state,
      action: PayloadAction<{ checked: boolean; datasetId?: string }>
    ) => {
      const { checked, datasetId } = action.payload;
      state.isShowMarkerLabel = checked;
      if (!state.catchmentSettingsTemp.datasetConfig || !datasetId) return;
      const labelConfig =
        state.catchmentSettingsTemp.datasetConfig.datasets[datasetId].dataset
          .style.labelConfig;
      labelConfig.show_label = checked;
    },
    setAdminAreaAddress: (
      state,
      action: PayloadAction<AdminAreaSettings | null>
    ) => {
      state.adminAreaSettings = {
        ...state.adminAreaSettings,
        ...action.payload
      };
    },
    setCatchmentSettings: (
      state,
      action: PayloadAction<CatchmentSettings | null>
    ) => {
      state.catchmentSettings = {
        ...state.catchmentSettings,
        ...action.payload
      };
    },
    setAdminAreaSettingsData: (
      state,
      action: PayloadAction<AdminAreaData | null>
    ) => {
      state.adminAreaSettingsData = action.payload;
    },
    setAdminAreaSettingsDataTemp: (
      state,
      action: PayloadAction<AdminAreaData | null>
    ) => {
      state.adminAreaSettingsDataTemp = action.payload;
    },
    setCurrRunningAnalysisId: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      state.currRunningAnalysisId = action.payload;
    },
    setAreaSize: (state, action: PayloadAction<number>) => {
      state.spatialConfig.areaSize = action.payload;
    },
    clearCurrRunningAnalysisId: (state) => {
      state.currRunningAnalysisId = undefined;
    },
    setLatestAnalysisId: (state, action: PayloadAction<string | undefined>) => {
      state.latestAnalysisId = action.payload;
    },
    clearLatestAnalysisId: (state) => {
      state.latestAnalysisId = undefined;
    },
    setCatchmentSettingsTemp: (
      state,
      action: PayloadAction<CatchmentSettings | null>
    ) => {
      state.catchmentSettingsTemp = {
        ...state.catchmentSettingsTemp,
        ...action.payload
      };
    },
    savePolygonSettings: (state) => {
      state.polygonSettings = state.polygonSettingsTemp;
    },
    clearPolygonSettingsTemp: (state) => {
      state.polygonSettingsTemp = {};
    },
    removePolygonItemSettingsTemp: (state, action: PayloadAction<string>) => {
      const polygons = { ...(state.polygonSettingsTemp ?? {}) };
      delete polygons[action.payload];

      state.polygonSettingsTemp = polygons;
    },
    setPolygonSettingsTemp: (
      state,
      action: PayloadAction<Record<string, PolygonSettingItem>>
    ) => {
      state.polygonSettingsTemp = action.payload;
    },
    updatePolygonSettingsTemp: (
      state,
      action: PayloadAction<
        Partial<PolygonSettingItem> & Pick<PolygonSettingItem, "id">
      >
    ) => {
      state.polygonSettingsTemp = {
        ...state.polygonSettingsTemp,
        [action.payload.id]: {
          ...state.polygonSettingsTemp[action.payload.id],
          ...action.payload
        }
      };
    },
    editGeometryPolygonSettingsTemp: (
      state,
      action: PayloadAction<{
        polygon: Geometry;
        id: string;
      }>
    ) => {
      state.polygonSettingsTemp = {
        ...state.polygonSettingsTemp,
        [action.payload.id]: {
          ...state.polygonSettingsTemp[action.payload.id],
          polygon: {
            ...state.polygonSettingsTemp[action.payload.id].polygon,
            geometry: action.payload.polygon
          }
        }
      };
    },
    addPolygonSettingsTemp: (
      state,
      action: PayloadAction<PolygonSettingItem>
    ) => {
      state.polygonSettingsTemp = {
        ...state.polygonSettingsTemp,
        [action.payload.id]: action.payload
      };
    },
    setCatchmentSettingsTempRoad: (
      state,
      action: PayloadAction<RoadAccessConfig | null>
    ) => {
      state.catchmentSettingsTemp.roadAccessConfig = {
        ...state.catchmentSettingsTemp.roadAccessConfig,
        ...action.payload
      };
    },
    addPlaceCoordinateTemp: (state, action: PayloadAction<MarkerPinItem>) => {
      const place = state.catchmentSettingsTemp.placeCoordinates ?? [];
      place.push({
        ...action.payload,
        id: uuidv4()
      });
      state.catchmentSettingsTemp.placeCoordinates = place;
    },
    removeCoordinateTemp: (state) => {
      state.catchmentSettingsTemp.placeCoordinates = [];
    },
    removeItemCoordinateTemp: (state, action: PayloadAction<string>) => {
      const place = state.catchmentSettingsTemp.placeCoordinates ?? [];
      const newArr = place.filter((item) => item.id !== action.payload);
      state.catchmentSettingsTemp.placeCoordinates = newArr;
    },
    updateCoordinateItemTemp: (state, action: PayloadAction<MarkerPinItem>) => {
      const { placeCoordinates = [] } = state.catchmentSettingsTemp;
      if (!placeCoordinates) return;

      const index = placeCoordinates.findIndex(
        (item) => item.id === action.payload.id
      );
      if (
        index === -1 ||
        !state.catchmentSettingsTemp.placeCoordinates?.[index]
      )
        return;

      state.catchmentSettingsTemp.placeCoordinates[index] = {
        ...placeCoordinates[index],
        ...action.payload
      };
    },
    clearCatchmentSettingsTemp: (state) => {
      state.catchmentSettingsTemp = initialCatchmentSetting;
    },

    setSpatialConfig: (state, action: PayloadAction<SpatialConfig>) => {
      state.spatialConfig = {
        ...state.spatialConfig,
        ...action.payload
      };
    },
    setSpatialState: (_, action: PayloadAction<SpatialState>) => {
      return action.payload;
    },
    setLatestSaveAsDataset: (
      state,
      action: PayloadAction<DatasetEntryFormData | null>
    ) => {
      state.latestSaveAsDataset = action.payload;
    },
    resetSpatialState: () => spatialInitialState,
    setDatasetConfigTemp: (
      state,
      action: PayloadAction<{
        uniqueId: string;
        originalId?: string;
        nextIndex?: number;
        dataset?: Dataset;
        rawDataset?: DatasetData;
        filters?: DatasetFilter[];
      }>
    ) => {
      if (!state.catchmentSettingsTemp.datasetConfig) return;

      const { uniqueId, originalId, nextIndex, dataset, rawDataset, filters } =
        action.payload;

      if (nextIndex && originalId) {
        state.catchmentSettingsTemp.datasetConfig.datasetIndices = {
          ...state.catchmentSettingsTemp.datasetConfig.datasetIndices,
          [originalId]: {
            counter:
              (state.catchmentSettingsTemp.datasetConfig.datasetIndices[
                originalId
              ]?.counter || 0) + 1,
            usedIndices: [
              ...(state.catchmentSettingsTemp.datasetConfig.datasetIndices[
                originalId
              ]?.usedIndices || []),
              nextIndex
            ]
          }
        };
      }

      if (rawDataset) {
        const datasets = state.catchmentSettingsTemp.datasetConfig.datasets;
        const existing = datasets[uniqueId] ?? {};
        const existingStyle = existing.dataset?.style ?? {};
        const existingRank = existingStyle.rankConfig;

        state.catchmentSettingsTemp.datasetConfig.datasets = {
          ...datasets,
          [uniqueId]: {
            ...existing,
            rawDataset,
            dataset: {
              ...existing.dataset,
              style: {
                ...existingStyle,
                rankConfig: {
                  ...existingRank,
                  isEnabled: existingRank?.isEnabled ?? false,
                  maxRanking: Math.min(
                    existingRank?.maxRanking ?? 1,
                    rawDataset.rows.length
                  )
                }
              }
            }
          }
        };
      }

      if (dataset) {
        const datasets = state.catchmentSettingsTemp.datasetConfig.datasets;
        const existing = datasets[uniqueId] ?? {};
        const style = dataset?.style ?? {};
        const rankConfig = style.rankConfig;

        state.catchmentSettingsTemp.datasetConfig.datasets = {
          ...datasets,
          [uniqueId]: {
            ...existing,
            dataset: {
              ...dataset,
              style: {
                ...style,
                rankConfig: {
                  ...rankConfig,
                  isEnabled: rankConfig?.isEnabled ?? false,
                  maxRanking: Math.min(
                    rankConfig?.maxRanking ?? 1,
                    rawDataset?.rows?.length ?? 1
                  )
                }
              }
            }
          }
        };

        if (
          !state.catchmentSettingsTemp.datasetConfig.visualizationOrder.includes(
            dataset.data.id
          )
        ) {
          state.catchmentSettingsTemp.datasetConfig.visualizationOrder =
            Array.isArray(
              state.catchmentSettingsTemp.datasetConfig.visualizationOrder
            )
              ? [
                  dataset.data.id,
                  ...state.catchmentSettingsTemp.datasetConfig
                    .visualizationOrder
                ]
              : [dataset.data.id];
        }
      }

      if (filters) {
        state.catchmentSettingsTemp.datasetConfig.datasets = {
          ...state.catchmentSettingsTemp.datasetConfig.datasets,
          [uniqueId]: {
            ...state.catchmentSettingsTemp.datasetConfig.datasets[uniqueId],
            filters
          }
        };
      }
    },
    changeSelectedAttribute: (
      state,
      action: PayloadAction<{ id: string; attribute: DatasetAttributeSchema }>
    ) => {
      if (!state.catchmentSettingsTemp.datasetConfig) return;

      const { id, attribute } = action.payload;
      state.catchmentSettingsTemp.datasetConfig.datasets[id] = {
        ...state.catchmentSettingsTemp.datasetConfig.datasets[id],
        dataset: {
          ...state.catchmentSettingsTemp.datasetConfig.datasets[id].dataset,
          selectedCategory: attribute
        }
      };
      const dataset =
        state.catchmentSettingsTemp.datasetConfig.datasets[id].dataset;
      const labelConfig = dataset.style.labelConfig;
      labelConfig.attribute_id = attribute.id;
      labelConfig.attribute_name = attribute.attribute_name;
      labelConfig.code = attribute.code;

      const stroke = dataset.style.stroke;
      stroke.attribute_id = attribute.id;
      stroke.attribute_name = attribute.attribute_name;
      stroke.code = attribute.code;
    },
    setSelectedDatasetCatchment: (state, action: PayloadAction<Dataset>) => {
      if (!state.catchmentSettingsTemp.datasetConfig) return;

      state.catchmentSettingsTemp.datasetConfig.selectedDataset =
        action.payload;
    },
    clearSelectedDatasetCatchment: (state) => {
      if (!state.catchmentSettingsTemp.datasetConfig) return;

      state.catchmentSettingsTemp.datasetConfig.selectedDataset = null;
    },

    removeDatasetCatchmentById: (state, action: PayloadAction<string>) => {
      if (!state.catchmentSettingsTemp.datasetConfig) return;

      const datasetId = action.payload;
      const [baseId, indexStr] = datasetId.split("_");
      const index = Number.parseInt(indexStr);

      delete state.catchmentSettingsTemp.datasetConfig.datasets[datasetId];

      state.catchmentSettingsTemp.datasetConfig.visualizationOrder =
        state.catchmentSettingsTemp.datasetConfig.visualizationOrder.filter(
          (id) => id !== datasetId
        );
      if (state.catchmentSettingsTemp.datasetConfig.datasetIndices[baseId]) {
        state.catchmentSettingsTemp.datasetConfig.datasetIndices[
          baseId
        ].usedIndices =
          state.catchmentSettingsTemp.datasetConfig.datasetIndices[
            baseId
          ].usedIndices.filter((i) => i !== index);

        if (
          state.catchmentSettingsTemp.datasetConfig.datasetIndices[baseId]
            .usedIndices.length === 0
        ) {
          delete state.catchmentSettingsTemp.datasetConfig.datasetIndices[
            baseId
          ];
        }
      }
    },
    updateVisibilityDatasetCatchment: (
      state,
      action: PayloadAction<{
        id: string;
        isVisible: boolean;
      }>
    ) => {
      if (!state.catchmentSettingsTemp.datasetConfig) return;

      const { id, isVisible } = action.payload;

      state.catchmentSettingsTemp.datasetConfig.datasets[id] = {
        ...state.catchmentSettingsTemp.datasetConfig.datasets[id],
        dataset: {
          ...state.catchmentSettingsTemp.datasetConfig.datasets[id].dataset,
          properties: {
            ...state.catchmentSettingsTemp.datasetConfig.datasets[id].dataset
              .properties,
            isVisible
          }
        }
      };
    },
    updateCatchmentDatasetStyleEnabledRank: (
      state,
      action: PayloadAction<{
        datasetId: string;
        isEnabled: boolean;
      }>
    ) => {
      const { datasetId, isEnabled } = action.payload;

      if (
        state.catchmentSettingsTemp?.datasetConfig?.datasets?.[datasetId]
          ?.dataset?.style?.rankConfig
      ) {
        state.catchmentSettingsTemp.datasetConfig.datasets[
          datasetId
        ].dataset.style.rankConfig.isEnabled = isEnabled;
      }
    },

    updateCatchmentDatasetStyleMaxRanking: (
      state,
      action: PayloadAction<{
        datasetId: string;
        maxRanking: number;
      }>
    ) => {
      const { datasetId, maxRanking } = action.payload;

      if (
        state.catchmentSettingsTemp?.datasetConfig?.datasets?.[datasetId]
          ?.dataset?.style?.rankConfig
      ) {
        state.catchmentSettingsTemp.datasetConfig.datasets[
          datasetId
        ].dataset.style.rankConfig.maxRanking = maxRanking;
      }
    },

    // Admin Tree Settings
    setAdminTreeSettingsSelectedIds: (
      state,
      action: PayloadAction<string[]>
    ) => {
      state.adminTreeSettings.selectedAreaIds = action.payload;
    },

    // Temp Admin Tree Settings
    setAdminTreeSettingsTempSelectedIds: (
      state,
      action: PayloadAction<string[]>
    ) => {
      state.adminTreeSettingsTemp.selectedAreaIds = action.payload;
    },

    setDrawPolygonDataset: (
      state,
      action: PayloadAction<{
        name?: string;
        polygon: Feature<Geometry, GeoJsonProperties>;
      }>
    ) => {
      const { name, polygon } = action.payload;
      if (!name) {
        return;
      }
      state.drawnPolygon ??= {};
      state.drawnPolygon[name] = polygon;
    },

    clearDrawPolygonDataset: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      const name = action.payload;
      if (!name) {
        state.drawnPolygon = undefined;
        return;
      }
      if (state.drawnPolygon) {
        delete state.drawnPolygon[name];
        if (Object.keys(state.drawnPolygon).length === 0) {
          state.drawnPolygon = undefined;
        }
      }
    },
    setOverrideGlobalFilter: (state, action: PayloadAction<boolean>) => {
      state.overrideGlobalFilter = action.payload;
    }
  }
});

export const {
  setAreaInput,
  setAdminAreaAddress,
  setCatchmentSettings,
  addPlaceCoordinateTemp,
  removeCoordinateTemp,
  removeItemCoordinateTemp,
  updateCoordinateItemTemp,
  setCatchmentSettingsTemp,
  setCatchmentSettingsTempRoad,
  clearCatchmentSettingsTemp,
  setAdminAreaSettingsData,
  setAdminAreaSettingsDataTemp,
  setSpatialConfig,
  setSpatialState,
  resetSpatialState,
  setCurrRunningAnalysisId,
  clearCurrRunningAnalysisId,
  setLatestAnalysisId,
  clearLatestAnalysisId,
  setAreaSize,
  setLatestSaveAsDataset,
  setIsShowMarkerLabel,
  removePolygonItemSettingsTemp,
  clearPolygonSettingsTemp,
  editGeometryPolygonSettingsTemp,
  setPolygonSettingsTemp,
  addPolygonSettingsTemp,
  updatePolygonSettingsTemp,
  savePolygonSettings,
  setDatasetConfigTemp,
  changeSelectedAttribute,
  setSelectedDatasetCatchment,
  clearSelectedDatasetCatchment,
  removeDatasetCatchmentById,
  updateVisibilityDatasetCatchment,
  updateCatchmentDatasetStyleEnabledRank,
  updateCatchmentDatasetStyleMaxRanking,
  setAdminTreeSettingsSelectedIds,
  setAdminTreeSettingsTempSelectedIds,
  setDrawPolygonDataset,
  clearDrawPolygonDataset,
  setOverrideGlobalFilter
} = spatialSlice.actions;

export const spatialReducer = spatialSlice.reducer;
